import { CompanyUser } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { useCompanyContext } from '../providers';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

type UseAssignLicenseActionProps = {
    user: CompanyUser;
    /** Licenses already handed out company-wide, against the seats paid for. */
    assignedLicenses: number;
};

type UseAssignLicenseActionData = {
    /** Whether this viewer can assign this member a license right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    assignLicense: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Assigning a member a license: whether this viewer may do it, why not when
 * they may not, and the call itself. Kept together so a caller can wire up a
 * button — disabled state, tooltip, spinner and click — from one hook.
 */
export function useAssignLicenseAction(
    props: UseAssignLicenseActionProps
): UseAssignLicenseActionData {
    const { user, assignedLicenses } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();
    const { company } = useCompanyContext();

    const canManageLicences = user.capabilities.canManageLicences;
    const [isAllowed, reason] = useMemo(() => {
        // The API decides who is permitted to manage this member's licence,
        // and hands back a ready-to-show reason when they are not.
        if (!canManageLicences.allowed) {
            return [
                false,
                canManageLicences.reason ??
                    t('actions.assign_license.reason_not_permitted')
            ];
        }

        // Whether there is a licence to give is still ours to work out.
        if (!company.subscription) {
            return [false, t('actions.assign_license.reason_no_subscription')];
        }

        const hasActiveSubscription = [
            'active',
            'past_due',
            'trialing'
        ].includes(company.subscription.status);
        if (!hasActiveSubscription) {
            return [
                false,
                t('actions.assign_license.reason_subscription_inactive')
            ];
        }

        if (user.license) {
            return [false, t('actions.assign_license.reason_already_licensed')];
        }

        // A trial is not capped by the subscription quantity.
        const remainingLicenses =
            company.subscription.quantity - assignedLicenses;
        if (
            company.subscription.status !== 'trialing' &&
            remainingLicenses <= 0
        ) {
            return [false, t('actions.assign_license.reason_none_available')];
        }

        return [true, ''];
    }, [
        canManageLicences,
        company.subscription,
        user.license,
        assignedLicenses,
        t
    ]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `PATCH /v3/users/{id}/licenses`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'NOT_SUBSCRIBED')) {
                return t('actions.assign_license.error_not_subscribed');
            }
            if (isApiError(error, 'NO_MORE_LICENSES')) {
                return t('actions.assign_license.error_no_more_licenses');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('actions.assign_license.error_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.users
                .updateUserLicense(user.id, {
                    license: {
                        validFrom: new Date().toISOString(),
                        validTo: null
                    }
                })
                .then((response) => response.data.data.user),
        onSuccess: (user) => {
            addOrUpdateCompanyUsersCache(queryClient, user);

            UIToast.success(t('actions.assign_license.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        assignLicense: () => mutation.mutateAsync().then(() => {}),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
